import { Component, OnDestroy, OnInit } from '@angular/core';
import { HelperService, GroupService, ClaimsService, EventService } from 'app/services';
import { Router } from '@angular/router';
import { TaskGroupTableViewModel } from 'app/viewmodels';
import { Subscription } from 'rxjs';
import { HarvestAddComponent, HarvestGroupDeleteComponent } from 'app/dialogs';
import { MatDialog } from '@angular/material/dialog';
import { getDocs } from '@angular/fire/firestore';

import * as models from 'app/models';

@Component({
    selector: 'app-task-group-list',
    templateUrl: './task-group-list.component.html',
    styleUrls: ['./task-group-list.component.scss'],
    standalone: false
})
export class TaskGroupListComponent implements OnInit, OnDestroy {
  companySub: Subscription;

  /** Flat rows for the p-table: task-group VM + numeric date keys for sorting. */
  tableRows: Array<any> = [];
  tableLoading: boolean = true;

  constructor(
    private claimsService: ClaimsService,
    private dialog: MatDialog,
    private eventService: EventService,
    private helperService: HelperService,
    private groupService: GroupService,
    private router: Router,
  ) { }

  ngOnInit(): void {

    let companyId = this.helperService.currentCompanyId;

    if(companyId != null) {
      this.getTaskGroups();
    };

    //set up subscription to listen to company id changes
    this.companySub = this.helperService.getCurrentCompanyId().subscribe(data => {
      this.getTaskGroups();
    })
  };

  ngOnDestroy(): void {
    if(this.companySub != null){
      this.companySub.unsubscribe();
    }
  }

  async getTaskGroups() {
    this.tableRows = [];
    this.tableLoading = true;

    let taskGroupsList = await (this.groupService.getAll()).get().toPromise();

    let listModels: Array<any> = [];
    for (const groupData of taskGroupsList.docs) {
      let data = groupData.data()
      let taskGroupVM: TaskGroupTableViewModel = {
        id: groupData.id,
        name: data.name,
        startDate: data.startDate,
        endDate: data.endDate,
        completedTasks: data.completedTasks,
        totalTasks: data.totalTasks,
        color: data.color
      }

      if(taskGroupVM.startDate == null || taskGroupVM.endDate == null) {
        let eventDataList = await getDocs(this.eventService.get(groupData.id, 'groups'));
        eventDataList.forEach(eventData => {
          let dbEvent = eventData.data();

          let sorted = dbEvent.list.sort((a, b) => (a.startDateTime > b.startDateTime) ? 1 : -1)
          taskGroupVM.startDate = sorted[0].startDateTime
          taskGroupVM.endDate = sorted[sorted.length - 1].startDateTime
          taskGroupVM.totalTasks = (dbEvent.list.length || 0)
        });
      }

      listModels.push({
        ...(taskGroupVM as any),
        startDateMs: this.tsToMs(taskGroupVM.startDate),
        endDateMs: this.tsToMs(taskGroupVM.endDate),
      });
    }

    this.tableRows = listModels;
    this.tableLoading = false;
  }

  private tsToMs(ts: any): number {
    return ts?.seconds != null ? ts.seconds * 1000 : -1;
  }

  formatDate(ts: any): string {
    if (ts == null) { return ''; }
    return this.helperService.timestampToFormattedDateString(ts);
  }

  openTaskGroup(g: any) {
    if (g?.id) {
      this.router.navigate(['console', 'task-groups', g.id]);
    }
  }

  get totalTasks(): number {
    return (this.tableRows || []).reduce((sum, r) => sum + (Number(r.totalTasks) || 0), 0);
  }

  canEdit() : boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.editGroup);
  }

  addTaskGroup() {
    let dialogRef = this.dialog.open(HarvestAddComponent, {
      data: { addGroup: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      //reload list
      if(result.added){
        this.getTaskGroups();
      }
      console.log(JSON.stringify(result.id));
    })
  }

  deleteTaskGroup() {
    let dialogRef = this.dialog.open(HarvestGroupDeleteComponent, {
      panelClass: 'full-width-dialog',
      data: { hideHarvests: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getTaskGroups();
    })
  }

  exportAsCSV() {
    // Build the CSV from the visible p-table columns.
    const cols: Array<{ header: string, value: (r: any) => string }> = [
      { header: 'Name', value: r => r.name ?? '' },
      { header: 'Start Date', value: r => this.formatDate(r.startDate) },
      { header: 'End Date', value: r => this.formatDate(r.endDate) },
      { header: '# of Tasks', value: r => (r.totalTasks != null ? String(r.totalTasks) : '') },
    ];

    const esc = (s: any) => '"' + String(s ?? '').replace(/"/g, '""') + '"';
    const lines = [cols.map(c => esc(c.header)).join(',')];
    this.tableRows.forEach(r => lines.push(cols.map(c => esc(c.value(r))).join(',')));

    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'task-groups.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
}
